/*
 * The footer newsletter form, without the page reload.
 *
 * What it replaces. Shopify's `{% form 'customer' %}` posts to
 * /contact#ContactFooter and answers with a redirect back to the page carrying
 * `?customer_posted=true` and that fragment. The browser therefore reloads at
 * the top of the document, paints, and then jumps down to the form to show the
 * confirmation. From the footer that reads as the page throwing you to the top
 * and dragging you back, which is what it looks like because it is what it is.
 *
 * This posts the same fields to the same endpoint with fetch and reveals a
 * message that is already in the markup, so nothing moves.
 *
 * Progressive enhancement, in the same shape as ap-subscribe-toggle.js. The
 * form works before this file runs and if it never runs: without JavaScript the
 * native post still happens, the server still renders the confirmation, and the
 * fragment in the action is what scrolls the reader back to it. That fragment
 * is deliberately left in place for exactly that case.
 *
 * Three failure modes and what each does:
 *
 *   invalid email     Handed to the browser. checkValidity() runs first and we
 *                     do not preventDefault, so the native bubble appears and
 *                     nothing is posted.
 *   server rejects    Shopify re-renders the page with the error inside
 *                     #ContactFooter-error. That text is lifted out of the
 *                     response and shown in place.
 *   network fails     The submission is handed back to the browser with a
 *                     native submit. Reloading is worse than staying put and
 *                     better than swallowing someone's signup.
 */
class ApNewsletterForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.success = this.querySelector('[data-ap-newsletter-success]');
    this.error = this.querySelector('[data-ap-newsletter-error]');
    this.errorText = this.querySelector('[data-ap-newsletter-error-text]');
    this.button = this.querySelector('[type="submit"]');
    this.input = this.querySelector('input[type="email"]');

    /* Every hook has to be present. A partial match means the markup moved, and
       reloading is a better outcome than a control that swallows submissions. */
    if (!this.form || !this.success || !this.error || !this.errorText) return;

    this.form.addEventListener('submit', (event) => this.onSubmit(event));
  }

  async onSubmit(event) {
    if (!this.form.checkValidity()) return;
    event.preventDefault();

    this.setBusy(true);
    this.error.hidden = true;
    this.success.hidden = true;

    let response;
    let html;
    try {
      /*
       * URLSearchParams, not FormData. FormData sends multipart/form-data;
       * a browser posting this form natively sends url-encoded, and that is
       * what /contact is written to read.
       */
      response = await fetch(this.form.action, {
        method: 'POST',
        headers: { Accept: 'text/html' },
        body: new URLSearchParams(new FormData(this.form)),
      });
      html = await response.text();
    } catch (networkError) {
      this.form.submit();
      return;
    }

    this.setBusy(false);

    if (this.succeeded(response, html)) {
      this.showSuccess();
    } else {
      this.showError(this.readError(html));
    }
  }

  /*
   * Shopify signals a successful signup by redirecting to the referring page
   * with `customer_posted=true`. fetch follows the redirect, so the final URL
   * carries it. The body is checked as well because a proxy or an app that
   * rewrites the redirect would otherwise turn a real signup into an error.
   */
  succeeded(response, html) {
    if (response.url && response.url.indexOf('customer_posted=true') !== -1) return true;
    return response.ok && html.indexOf('customer_posted=true') !== -1;
  }

  readError(html) {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const node = doc.querySelector('#ContactFooter-error');
      const text = node ? node.textContent.trim() : '';
      if (text) return text;
    } catch (parseError) {
      /* fall through to the generic message */
    }
    return '';
  }

  showSuccess() {
    this.error.hidden = true;
    this.success.hidden = false;
    if (this.input) this.input.value = '';
  }

  showError(text) {
    this.success.hidden = true;
    this.errorText.textContent = text || this.dataset.genericError || 'That did not go through. Please try again.';
    this.error.hidden = false;
    if (this.input) this.input.focus();
  }

  /*
   * Focus is deliberately not moved on success. The message sits a few
   * millimetres from the button that was just pressed and both are on screen,
   * so moving focus buys nothing and can scroll the page, which is the whole
   * defect this file exists to remove. The message carries aria-live instead.
   */
  setBusy(busy) {
    if (!this.button) return;
    this.button.disabled = busy;
    this.button.setAttribute('aria-busy', busy ? 'true' : 'false');
  }
}

if (!customElements.get('ap-newsletter-form')) {
  customElements.define('ap-newsletter-form', ApNewsletterForm);
}
